// Per-chat canvas state, owned by a module-level store.
//
// Why a store and not React state: the chat agent's canvas_* tools
// need to read and write the canvas synchronously inside a tool's
// `execute()` — they have to return the new item's id, and they have
// to see the just-written state when a follow-up call (e.g.
// canvas_inspect) fires immediately after. React setState is async
// (the updater runs in the next batch), so both of those went wrong:
// the tool returned id=null because the updater hadn't run yet, and
// canvas_inspect read stale chatsRef.current and saw an empty canvas.
//
// Now the store is canonical for the agent. React mirrors it via
// useSyncExternalStore so the UI re-renders on every change. UI-side
// mutations (Remove / Clear buttons) also go through the store so
// every writer takes the same path.

import {
    normalizeCanvas,
    addItem as _addItem,
    updateItem as _updateItem,
    removeItem as _removeItem,
    clearItems as _clearItems,
    summariseCanvas as _summariseCanvas,
} from "./canvasState.js";
import { onAuthIdentityChange } from "./authTokenStore.js";

// Map values are already normalized — we hold onto the SAME reference
// until a write happens. useSyncExternalStore's getSnapshot must be
// stable across re-renders (compares by reference); creating a fresh
// `normalizeCanvas(...)` per read would cause an infinite update loop.
const stores = new Map();        // chatId → normalized { items: [...] }
const subscribers = new Map();   // chatId → Set<() => void>
// Global change subscribers — fire on EVERY canvas write across every
// chat. ChatConsole uses this to bump its save-debounce loop, which
// otherwise only re-arms on `setChats` and would miss canvas
// mutations entirely (canvas state doesn't live in React state).
const globalSubscribers = new Set();

function key(chatId) {
    return String(chatId ?? "default");
}

// Stable reference to the "empty" canvas — every chat that hasn't been
// written to shares this exact object, so React never sees a "change"
// just from a read.
const EMPTY_CANVAS = Object.freeze({ items: Object.freeze([]) });

export function readCanvas(chatId) {
    const k = key(chatId);
    if (!stores.has(k)) return EMPTY_CANVAS;
    return stores.get(k);
}

export function writeCanvas(chatId, next) {
    const k = key(chatId);
    stores.set(k, normalizeCanvas(next));
    notify(k);
    return stores.get(k);
}

// Apply a functional update synchronously. Returns the new state so
// the caller can read fields (e.g. the just-added id) without going
// through React.
export function mutateCanvas(chatId, mutator) {
    const cur = readCanvas(chatId);
    const next = typeof mutator === "function" ? mutator(cur) : mutator;
    return writeCanvas(chatId, next);
}

export function subscribeCanvas(chatId, fn) {
    const k = key(chatId);
    if (!subscribers.has(k)) subscribers.set(k, new Set());
    subscribers.get(k).add(fn);
    return () => {
        subscribers.get(k)?.delete(fn);
    };
}

function notify(k) {
    const subs = subscribers.get(k);
    if (subs) {
        for (const fn of subs) {
            try { fn(); } catch { /* swallow — one bad subscriber shouldn't block siblings */ }
        }
    }
    // Notify global subscribers too — used by persistence to re-arm
    // its save debounce when any chat's canvas changes.
    for (const fn of globalSubscribers) {
        try { fn(); } catch { /* swallow */ }
    }
}

onAuthIdentityChange(() => {
    const keys = [...stores.keys()];
    stores.clear();
    for (const k of keys) notify(k);
});

// Subscribe to ANY canvas change (across all chats). Used by the
// chat-state persistence loop: canvas writes happen outside React
// state, so without this the save effect — which only re-arms on
// `setChats` — would never see canvas mutations and the canvas would
// silently fail to persist.
export function subscribeAnyCanvas(fn) {
    globalSubscribers.add(fn);
    return () => {
        globalSubscribers.delete(fn);
    };
}

// Lifecycle helpers — call from chat lifecycle code so the store
// doesn't accumulate orphan entries.
export function destroyCanvas(chatId) {
    const k = key(chatId);
    stores.delete(k);
    subscribers.delete(k);
}

export function cloneCanvas(srcChatId, dstChatId) {
    const src = readCanvas(srcChatId);
    writeCanvas(dstChatId, { items: src.items.map((it) => ({ ...it })) });
}

// Higher-level mutators that compose with the pure helpers in
// canvasState.js. Each returns the same `{ items, ...maybeMeta }`
// shape as the underlying helper so callers can read e.g. addedId
// without doing the math themselves.
export function addCanvasItem(chatId, payload) {
    const cur = readCanvas(chatId);
    const result = _addItem(cur, payload);
    writeCanvas(chatId, { items: result.items });
    return result; // { items, addedId }
}

export function updateCanvasItem(chatId, id, partial) {
    const cur = readCanvas(chatId);
    const result = _updateItem(cur, id, partial);
    writeCanvas(chatId, { items: result.items });
    return result; // { items, found }
}

export function removeCanvasItem(chatId, id) {
    const cur = readCanvas(chatId);
    const result = _removeItem(cur, id);
    writeCanvas(chatId, { items: result.items });
    return result; // { items, found }
}

export function clearCanvasItems(chatId) {
    writeCanvas(chatId, _clearItems());
}

// Mark a canvas item as having been crystallised into a saved
// kernel. Called by the canvas-to-kernels save flow on each
// sub-agent's success. Persists across page refreshes (canvas state
// is in the chat record), so the "saved as /command" badge survives
// reloads and a re-save reuses the same command instead of suffixing.
export function markCanvasItemSaved(chatId, itemId, kernel) {
    if (!itemId || !kernel || typeof kernel.command !== "string") return { found: false };
    return updateCanvasItem(chatId, itemId, {
        savedKernel: { command: kernel.command, savedAt: Date.now() },
    });
}

export const summariseCanvas = _summariseCanvas;
